export const MODULE_LABELS = {
  DISCOVERY: "Discovery",
  PITCH: "Messaging / Pitch",
  OBJECTIONS: "Objections",
  COLD_CALL: "Outbound & Inbound",
  PLAYS: "Plays",
  ON_THE_FLY: "On-the-Fly Plays",
  MANAGEMENT: "Management Practice",
  CONVERSATION_ANALYSIS: "Conversation Analysis",
  DEAL_CLINIC: "Deal Clinic",
};

export const MODULE_IDS = {
  DISCOVERY: "clswpxzsl0001a4gpwdko3fys",
  PITCH: "clswpxzsm0003a4gpe0q9z4ma",
  OBJECTIONS: "clswpxzsl0000a4gpo97jqf1z",
  COLD_CALL: "clzvi5r3y0000o61swmun4rbw",
  PLAYS: "cm1ub45r80000vhsa89dnikud",
  ON_THE_FLY: "cmg9u6a4200006ocwui4sh3qd",
  // These synthetic IDs are the persisted moduleIds used by workspace module settings.
  MANAGEMENT: "management-practice-module-id",
  CONVERSATION_ANALYSIS: "conversation-analysis-module-id",
  DEAL_CLINIC: "dem-cog-module-id",
};

export const MODULE_HREFS = {
  DISCOVERY: "/simulator/discovery",
  PITCH: "/simulator/pitch",
  OBJECTIONS: "/simulator/objections",
  COLD_CALL: "/simulator/cold-call",
  PLAYS: "/simulator/plays",
  ON_THE_FLY: "/simulator/on-the-fly",
  CONVERSATION_ANALYSIS: "/dashboard/conversation-analyzer",
};

const DASHBOARD_TO_KEY = {
  "/dashboard/discovery": "DISCOVERY",
  "/dashboard/pitch": "PITCH",
  "/dashboard/objections": "OBJECTIONS",
  "/dashboard/cold-call": "COLD_CALL",
  "/dashboard/plays": "PLAYS",
  "/dashboard/plays/on-the-fly": "ON_THE_FLY",
  "/dashboard/management": "MANAGEMENT",
  "/dashboard/transcript-results": "CONVERSATION_ANALYSIS",
  "/dashboard/conversation-analyzer": "CONVERSATION_ANALYSIS",
  "/simulator/on-the-fly": "ON_THE_FLY",
  "/dem-cog/reports": "DEAL_CLINIC",
};

const REPORT_TO_KEY = {
  "/workspaces/reports/discovery": "DISCOVERY",
  "/workspaces/reports/pitch": "PITCH",
  "/workspaces/reports/objections": "OBJECTIONS",
  "/workspaces/reports/cold-call": "COLD_CALL",
  "/workspaces/reports/plays": "PLAYS",
  "/workspaces/reports/management": "MANAGEMENT",
  "/workspaces/reports/transcript-results": "CONVERSATION_ANALYSIS",
  "/dem-cog/reports": "DEAL_CLINIC",
};

const ADMIN_TO_KEY = {
  "/workspaces/admin/data/discovery": "DISCOVERY",
  "/workspaces/admin/data/pitch": "PITCH",
  "/workspaces/admin/data/objections": "OBJECTIONS",
  "/workspaces/admin/data/cold-call": "COLD_CALL",
  "/workspaces/admin/data/plays": "PLAYS",
  "/workspaces/admin/data/management-scenarios": "MANAGEMENT",
  "/dem-cog/dems": "DEAL_CLINIC",
};

const PLAYBOOK_TO_KEY = {
  messaging: "PITCH",
  objections: "OBJECTIONS",
  discovery: "DISCOVERY",
  "cold-call": "COLD_CALL",
  plays: "PLAYS",
};

function normalize(value) {
  return typeof value === "string" ? value.trim() : "";
}

function getModuleTitle(module) {
  return normalize(module?.title) || normalize(module?.name);
}

export function getModuleLabelByKey(enabledModules = [], key, fallback) {
  if (!key) return fallback;
  const moduleId = MODULE_IDS[key];
  const moduleHref = MODULE_HREFS[key];
  const fallbackLabel = fallback || MODULE_LABELS[key] || "";
  const match = enabledModules.find((module) => {
    if (moduleId && module?.id === moduleId) return true;
    if (moduleHref && module?.href === moduleHref) return true;
    const defaultName = normalize(module?.name).toLowerCase();
    const defaultTitle = normalize(module?.title).toLowerCase();
    return Boolean(
      fallbackLabel &&
        (defaultName === fallbackLabel.toLowerCase() ||
          defaultTitle === fallbackLabel.toLowerCase())
    );
  });

  return getModuleTitle(match) || fallbackLabel;
}

export function isModuleEnabledByKey(enabledModules = [], key) {
  if (!key) return false;
  const moduleId = MODULE_IDS[key];
  const moduleHref = MODULE_HREFS[key];
  const canonicalLabel = MODULE_LABELS[key] || "";

  return enabledModules.some((module) => {
    if (moduleId && module?.id === moduleId) return true;
    if (moduleHref && module?.href === moduleHref) return true;
    // Legacy CONVERSATION_ANALYSIS records sometimes carry a `resolver` value
    // instead of the canonical name. Keep that path as a fallback.
    if (key === "CONVERSATION_ANALYSIS") {
      const resolver = normalize(module?.resolver).toLowerCase();
      if (["conversationanalysis", "conversation_analysis"].includes(resolver)) {
        return true;
      }
    }
    return Boolean(
      canonicalLabel &&
        normalize(module?.name).toLowerCase() === canonicalLabel.toLowerCase()
    );
  });
}

export function getModuleLabelForDashboardHref(enabledModules = [], href, fallback) {
  return getModuleLabelByKey(enabledModules, DASHBOARD_TO_KEY[href], fallback);
}

export function getModuleLabelForReportHref(enabledModules = [], href, fallback) {
  return getModuleLabelByKey(enabledModules, REPORT_TO_KEY[href], fallback);
}

export function getModuleLabelForAdminHref(enabledModules = [], href, fallback) {
  return getModuleLabelByKey(enabledModules, ADMIN_TO_KEY[href], fallback);
}

export function getModuleLabelForPlaybookId(enabledModules = [], id, fallback) {
  return getModuleLabelByKey(enabledModules, PLAYBOOK_TO_KEY[id], fallback);
}

export function getModuleLabelForScenarioType(enabledModules = [], type, fallback) {
  const normalizedType = normalize(type).toUpperCase();
  const scenarioTypeToKey = {
    "COLD-CALL": "COLD_CALL",
    COLD_CALL: "COLD_CALL",
    PLAY: "PLAYS",
  };
  const key = scenarioTypeToKey[normalizedType] || normalizedType;
  return getModuleLabelByKey(enabledModules, key, fallback);
}
